import React, { useEffect, useMemo } from "react";
import { HiOutlineDownload } from "react-icons/hi";
import {
  FaFilePdf,
  FaFileWord,
  FaFileImage,
  FaFileAlt,
  FaFile,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileCsv,
  FaFont,
} from "react-icons/fa";
import { FadeLoader } from "react-spinners";
import InfiniteScroll from "../../../../../Components/InfiniteScroll/InfiniteScroll";

const getFileExtension = (url) => {
  return url?.split(".").pop().toLowerCase();
};

const FileIcon = ({ url }) => {
  const ext = getFileExtension(url);

  const iconMap = {
    pdf: <FaFilePdf className="size-7 text-red-500" />,
    doc: <FaFileWord className="size-7 text-blue-600" />,
    docx: <FaFileWord className="size-7 text-blue-600" />,
    jpg: <FaFileImage className="size-7 text-green-500" />,
    png: <FaFileImage className="size-7 text-green-500" />,
    txt: <FaFileAlt className="size-7 text-gray-500" />,
    xls: <FaFileExcel className="size-20 text-green-600" />,
    xlsx: <FaFileExcel className="size-20 text-green-600" />,
    ppt: <FaFilePowerpoint className="size-20 text-orange-500" />,
    pptx: <FaFilePowerpoint className="size-20 text-orange-500" />,
    csv: <FaFileCsv className="size-20 text-blue-400" />,
    ttf: <FaFont className="size-20 text-purple-500" />,
    otf: <FaFont className="size-20 text-purple-500" />,
    ttc: <FaFont className="size-20 text-purple-500" />,
  };

  return iconMap[ext] || <FaFile className="text-gray-400" />;
};

// Reusable Card Component
const DocumentCard = ({ doc, withDownload = true }) => {
  const handleDownload = () => {
    const ext = getFileExtension(doc.mediaUrl);
    if (ext === "pdf") {
      window.open(doc.mediaUrl, "_blank");
    } else {
      const a = document.createElement("a");
      a.href = doc.mediaUrl;
      a.download = doc.mediaUrl;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <div className="flex items-start justify-between gap-3 border border-[#EBEBEB] rounded-xl p-2 w-full max-w-md">
      <div className="flex flex-grow-1 items-start gap-3">
        <div
          className={`min-w-[32px] min-h-[42px] max-w-[32px] max-h-[42px] flex items-center justify-center rounded-lg`}
        >
          <FileIcon url={doc.mediaUrl} />
        </div>
        <div className="flex-grow-1">
          <h3 className="font-semibold text-sm truncate w-[270px]">
            {doc.mediaId.fileUrl.split("/").pop()}
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            {getFileExtension(doc.mediaUrl).toUpperCase()}
          </p>
        </div>
      </div>
      {withDownload && (
        <div>
          <button
            onClick={handleDownload}
            className="flex items-center justify-center shadow-md rounded-full border-none h-10 w-10 bg-white"
          >
            <HiOutlineDownload className="text-[var(--primary-color)] text-2xl" />
          </button>
        </div>
      )}
    </div>
  );
};

export default function Documents({
  documentsList,
  isMediaLoading,
  getMediaList,
  documentsCurrentPage,
  documentsTotalPage,
  chatConversationId,
}) {
  useEffect(() => {
    getMediaList("DOCUMENT");
  }, [chatConversationId]);
  const groupedData = useMemo(() => {
    const groupedData = {};
    documentsList.forEach((item) => {
      const date = new Date(item.sentTime).toLocaleDateString("en-US", {
        timeZone: "Asia/Kolkata",
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
      groupedData[date] = groupedData[date] || [];
      groupedData[date].push(item);
    });
    return groupedData;
  }, [documentsList]);

  const fetchMoreDocuments = async () => {
    if (documentsCurrentPage < documentsTotalPage) {
      await getMediaList("DOCUMENT", documentsCurrentPage + 1);
    }
  };

  return (
    <section className="">
      {isMediaLoading ? (
        <div className="flex justify-center items-center h-full mt-5">
          <FadeLoader color="#7104F8" size={15} margin={2} />
        </div>
      ) : documentsList.length <= 0 ? (
        <div className="flex justify-center items-center h-full">
          <p className="text-gray-500">No documents found</p>
        </div>
      ) : (
        <InfiniteScroll
          dataLength={documentsList.length}
          next={fetchMoreDocuments}
          hasMore={documentsCurrentPage < documentsTotalPage}
          loader={
            <p className="text-gray-500 text-center">
              Loading more documents...
            </p>
          }
        >
          {Object.entries(groupedData)
            .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
            .map(([date, docs], idx) => (
              <div className={idx !== 0 ? "my-4" : ""} key={date}>
                <label className="text-sm font-semibold">{date}</label>
                <div className="flex flex-col gap-3 mt-2">
                  {docs.map((doc, index) => (
                    <DocumentCard key={index} doc={doc} />
                  ))}
                </div>
              </div>
            ))}
        </InfiniteScroll>
      )}
    </section>
  );
}
